import React from "react";
import { FormattedMessage } from "react-intl";

import {
	hunCountryCode,
	hunPostCodeToPlace
} from "lib/build/concepts/location";
import { Validator, Value } from "lib/build/schema";

import { Button } from "src/common/components/Button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle
} from "src/common/components/Card";
import {
	Fieldset,
	FieldsetGrid,
	Form,
	FormBody,
	FormFooter,
	Legend
} from "src/common/components/Form";
import { SplitColumn, SplitRow } from "src/common/components/Grid";
import { FormPage } from "src/common/components/Page";
import {
	FieldData,
	FieldListData,
	FieldsetData,
	FieldsetRenderer,
	FormContextProvider,
	FormState,
	useComputedValue,
	useFormState
} from "src/features/forms";
import { PatientLink } from "src/features/patient";
import { genericMessages, sexMessages } from "src/messages/generic";
import { messages } from "src/views/patient-form/messages";

export function usePatientForm(
	validator: Validator,
	initialValue?: { value: Value }
): FormState {
	const formState = useFormState({ validator, initialValue });

	useComputedValue(computeMaidenName, formState, {
		sources: [
			["value", "countryOfBirth"],
			["value", "firstName"],
			["value", "lastName"]
		],
		target: ["value", "maidenName"]
	});

	useComputedValue(computeCity, formState, {
		sources: [
			["value", "address", "country"],
			["value", "address", "postCode"]
		],
		target: ["value", "address", "city"]
	});

	return formState;
}

function computeMaidenName(
	countryOfBirth: string | undefined,
	firstName: string | undefined,
	lastName: string | undefined
): string | undefined {
	if (!firstName || !lastName) {
		return;
	}
	if (countryOfBirth === hunCountryCode) {
		return `${lastName} ${firstName}`;
	} else {
		return `${lastName} ${firstName}`;
	}
}

function computeCity(
	country: string | undefined,
	postCode: string | undefined
): string | undefined {
	if (!country || !postCode || country !== hunCountryCode) {
		return;
	}
	return hunPostCodeToPlace.get(postCode);
}

const errorMessages = {
	socialSecurityNumber: {
		presence: messages.socialSecurityNumberRequired,
		format: messages.socialSecurityNumberFormat,
		unique: messages.socialSecurityNumberUsed
	},
	patientHospitalNumber: {
		presence: messages.patientHospitalNumberRequired,
		format: messages.patientHospitalNumberFormat
	},
	dateOfBirth: { presence: messages.dateOfBirthRequired },
	countryOfBirth: { presence: messages.countryOfBirthRequired },
	placeOfBirth: {
		presence: messages.placeOfBirthRequired,
		value: messages.placeOfBirthValue
	},
	sex: { presence: messages.sexRequired },
	firstName: { presence: messages.firstNameRequired },
	lastName: { presence: messages.lastNameRequired },
	maidenName: { presence: messages.maidenNameRequired },
	maidenNameOfMother: { presence: messages.maidenNameOfMotherRequired },
	phoneNumbers: {
		presence: messages.phoneNumberRequired,
		format: messages.phoneNumberFormat
	},
	emails: { presence: messages.emailRequired, format: messages.emailFormat },
	country: { presence: messages.countryRequired },
	county: { presence: messages.countyRequired, value: messages.countyValue },
	postCode: {
		presence: messages.postCodeRequired,
		value: messages.postCodeValue
	},
	city: { presence: messages.cityRequired, value: messages.cityValue },
	addressLineOne: { presence: messages.addressRequired },
	user: { presence: messages.userRequired },
	stampNumber: {
		presence: messages.stampNumberRequired,
		format: messages.stampNumberFormat
	}
};

const patientFields: {
	[key: string]: FieldListData | FieldData;
} = {
	socialSecurityNumber: {
		type: "field",
		path: ["value", "socialSecurityNumber"],
		label: messages.socialSecurityNumber,
		errorMessages: errorMessages.socialSecurityNumber,
		help: messages.socialSecurityNumberHelp,
		input: {
			type: "input",
			props: { type: "social-security-number", disabled: true }
		}
	},
	patientHospitalNumber: {
		type: "field",
		path: ["value", "patientHospitalNumber"],
		label: messages.patientHospitalNumber,
		help: messages.patientHospitalNumberHelp,
		errorMessages: errorMessages.patientHospitalNumber,
		input: { type: "input", props: { type: "text" } }
	},
	dateOfBirth: {
		type: "field",
		path: ["value", "dateOfBirth"],
		label: messages.dateOfBirth,
		errorMessages: errorMessages.dateOfBirth,
		input: { type: "input", props: { type: "date" } }
	},
	countryOfBirth: {
		type: "field",
		path: ["value", "countryOfBirth"],
		label: messages.countryOfBirth,
		errorMessages: errorMessages.countryOfBirth,
		input: { type: "country", props: {} }
	},
	placeOfBirth: {
		type: "field",
		path: ["value", "placeOfBirth"],
		label: messages.placeOfBirth,
		errorMessages: errorMessages.placeOfBirth,
		input: { type: "input", props: { type: "text" } }
	},
	sex: {
		type: "field",
		path: ["value", "sex"],
		label: messages.sex,
		errorMessages: errorMessages.sex,
		input: { type: "button-group", props: { labels: sexMessages } }
	},
	title: {
		type: "field",
		path: ["value", "title"],
		label: messages.title,
		errorMessages: {},
		input: { type: "input", props: { type: "text" } }
	},
	firstName: {
		type: "field",
		path: ["value", "firstName"],
		label: messages.firstName,
		errorMessages: errorMessages.firstName,
		input: { type: "input", props: { type: "text" } }
	},
	lastName: {
		type: "field",
		path: ["value", "lastName"],
		label: messages.lastName,
		errorMessages: errorMessages.lastName,
		input: { type: "input", props: { type: "text" } }
	},
	maidenName: {
		type: "field",
		path: ["value", "maidenName"],
		label: messages.maidenName,
		errorMessages: errorMessages.maidenName,
		input: { type: "input", props: { type: "text" } }
	},
	maidenNameOfMother: {
		type: "field",
		path: ["value", "maidenNameOfMother"],
		label: messages.maidenNameOfMother,
		errorMessages: errorMessages.maidenNameOfMother,
		input: { type: "input", props: { type: "text" } }
	},
	phoneNumbers: {
		type: "field-list",
		path: ["value", "phoneNumbers"],
		label: messages.phoneNumber,
		errorMessages: errorMessages.phoneNumbers,
		input: { type: "input", props: { type: "phone-number" } }
	},
	emails: {
		type: "field-list",
		path: ["value", "emails"],
		label: messages.email,
		errorMessages: errorMessages.emails,
		input: { type: "input", props: { type: "text" } }
	},
	country: {
		type: "field",
		path: ["value", "address", "country"],
		label: messages.country,
		errorMessages: errorMessages.country,
		input: { type: "country", props: {} }
	},
	county: {
		type: "field",
		path: ["value", "address", "county"],
		label: messages.county,
		errorMessages: errorMessages.county,
		input: { type: "county", props: {} }
	},
	postCode: {
		type: "field",
		path: ["value", "address", "postCode"],
		label: messages.postCode,
		errorMessages: errorMessages.postCode,
		input: { type: "input", props: { type: "text" } }
	},
	city: {
		type: "field",
		path: ["value", "address", "city"],
		label: messages.city,
		errorMessages: errorMessages.city,
		input: { type: "input", props: { type: "text" } }
	},
	addressLineOne: {
		type: "field",
		path: ["value", "address", "addressLineOne"],
		label: messages.addressLineOne,
		errorMessages: errorMessages.addressLineOne,
		input: { type: "input", props: { type: "text" } }
	},
	addressLineTwo: {
		type: "field",
		path: ["value", "address", "addressLineTwo"],
		label: messages.addressLineTwo,
		errorMessages: {},
		input: { type: "input", props: { type: "text" } }
	},
	connectedUsers: {
		type: "field-list",
		path: ["value", "connectedUsers"],
		label: messages.user,
		errorMessages: errorMessages.user,
		input: { type: "remote-select", props: {} }
	}
};

const generalPractitionerFields: {
	[key: string]: FieldListData | FieldData;
} = {
	hasGeneralPractitioner: {
		type: "field",
		path: ["value", "hasGeneralPractitioner"],
		label: messages.hasGeneralPractitioner,
		errorMessages: {},
		input: { type: "switch", props: {} }
	},
	title: {
		type: "field",
		path: ["value", "generalPractitioner", "title"],
		label: messages.title,
		errorMessages: {},
		input: { type: "input", props: { type: "text" } }
	},
	firstName: {
		type: "field",
		path: ["value", "generalPractitioner", "firstName"],
		label: messages.firstName,
		errorMessages: errorMessages.firstName,
		input: { type: "input", props: { type: "text" } }
	},
	lastName: {
		type: "field",
		path: ["value", "generalPractitioner", "lastName"],
		label: messages.lastName,
		errorMessages: errorMessages.lastName,
		input: { type: "input", props: { type: "text" } }
	},
	phoneNumbers: {
		type: "field-list",
		path: ["value", "generalPractitioner", "phoneNumbers"],
		label: messages.phoneNumber,
		errorMessages: errorMessages.phoneNumbers,
		input: { type: "input", props: { type: "phone-number" } }
	},
	emails: {
		type: "field-list",
		path: ["value", "generalPractitioner", "emails"],
		label: messages.email,
		errorMessages: errorMessages.emails,
		input: { type: "input", props: { type: "text" } }
	},
	stampNumber: {
		type: "field",
		path: ["value", "generalPractitioner", "stampNumber"],
		label: messages.stampNumber,
		errorMessages: errorMessages.stampNumber,
		input: { type: "input", props: { type: "text" } }
	},
	medicalPractitionerHospitalNumber: {
		type: "field",
		path: [
			"value",
			"generalPractitioner",
			"medicalPractitionerHospitalNumber"
		],
		label: messages.medicalPractitionerHospitalNumber,
		errorMessages: {},
		input: { type: "input", props: { type: "text" } }
	}
};

const personalData: FieldsetData = {
	rows: [
		{
			columns: [
				{ width: 4, field: patientFields.socialSecurityNumber },
				{ width: 4, field: patientFields.patientHospitalNumber },
				{ width: 4, field: patientFields.sex }
			]
		},
		{
			columns: [
				{ width: 2, field: patientFields.title },
				{ width: 5, field: patientFields.lastName },
				{ width: 5, field: patientFields.firstName }
			]
		},
		{
			columns: [
				{ width: 6, field: patientFields.maidenName },
				{ width: 6, field: patientFields.maidenNameOfMother }
			]
		},
		{
			columns: [
				{ width: 4, field: patientFields.dateOfBirth },
				{ width: 4, field: patientFields.countryOfBirth },
				{ width: 4, field: patientFields.placeOfBirth }
			]
		}
	]
};

const contactDetails: FieldsetData = {
	rows: [
		{
			columns: [
				{ width: 6, field: patientFields.country },
				{ width: 6, field: patientFields.county }
			]
		},
		{
			columns: [
				{ width: 6, field: patientFields.postCode },
				{ width: 6, field: patientFields.city }
			]
		},
		{
			columns: [
				{ width: 6, field: patientFields.addressLineOne },
				{ width: 6, field: patientFields.addressLineTwo }
			]
		},
		{
			columns: [
				{ width: 6, field: patientFields.phoneNumbers },
				{ width: 6, field: patientFields.emails }
			]
		}
	]
};

const physician: FieldsetData = {
	rows: [{ columns: [{ width: 12, field: patientFields.connectedUsers }] }]
};

const generalPractitioner: FieldsetData = {
	rows: [
		{
			columns: [
				{
					width: 12,
					field: generalPractitionerFields.hasGeneralPractitioner
				}
			]
		},
		{
			columns: [
				{ width: 2, field: generalPractitionerFields.title },
				{ width: 5, field: generalPractitionerFields.firstName },
				{ width: 5, field: generalPractitionerFields.lastName }
			]
		},
		{
			columns: [
				{ width: 6, field: generalPractitionerFields.phoneNumbers },
				{ width: 6, field: generalPractitionerFields.emails }
			]
		},
		{
			columns: [
				{ width: 12, field: generalPractitionerFields.stampNumber }
			]
		},
		{
			columns: [
				{
					width: 12,
					field: generalPractitionerFields.medicalPractitionerHospitalNumber
				}
			]
		}
	]
};

interface PatientFormProps {
	titleText: React.ReactNode;
	submitText: React.ReactNode;
	formState: FormState;
	isSubmitted: boolean;
	isLoading: boolean;
	onSubmit(): unknown;
}

export const PatientForm: React.FunctionComponent<PatientFormProps> = ({
	titleText,
	submitText,
	formState,
	isSubmitted,
	isLoading,
	onSubmit
}) => (
	<FormPage>
		<Card>
			<CardHeader>
				<CardTitle>{titleText}</CardTitle>
			</CardHeader>
			<CardContent>
				<FormContextProvider {...formState}>
					<Form isSubmitted={isSubmitted} onSubmit={onSubmit}>
						<FormBody>
							<FieldsetGrid>
								<Fieldset>
									<Legend>
										<FormattedMessage
											{...messages.personalData}
										/>
									</Legend>
									<FieldsetRenderer {...personalData} />
								</Fieldset>
								<Fieldset>
									<Legend>
										<FormattedMessage
											{...messages.contactDetails}
										/>
									</Legend>
									<FieldsetRenderer {...contactDetails} />
								</Fieldset>
								<Fieldset>
									<Legend>
										<FormattedMessage
											{...messages.connectedUsers}
										/>
									</Legend>
									<FieldsetRenderer {...physician} />
								</Fieldset>
								<Fieldset>
									<Legend>
										<FormattedMessage
											{...messages.generalPractitionerData}
										/>
									</Legend>
									<FieldsetRenderer
										{...generalPractitioner}
									/>
								</Fieldset>
							</FieldsetGrid>
						</FormBody>
						<FormFooter>
							<SplitRow>
								<SplitColumn side="left">
									<PatientLink
										to="/dashboard"
										className="button button--secondary"
									>
										<FormattedMessage
											{...genericMessages.cancel}
										/>
									</PatientLink>
								</SplitColumn>
								<SplitColumn side="right">
									<Button type="submit" isLoading={isLoading}>
										{submitText}
									</Button>
								</SplitColumn>
							</SplitRow>
						</FormFooter>
					</Form>
				</FormContextProvider>
			</CardContent>
		</Card>
	</FormPage>
);
